import { NAVIGATION_ACTIONS, STATUS_TYPES } from './_const';

/**
 * Check for a particular search parameter and inject it as the value into form state
 * @param {string} key target key to check
 * @param {Array} values list of form values
 * @param {URLSearchParams} searchParams search parameters from the URL
 * @param {string} paramKey target query param to check for
 * @returns updated `values` array
 */
export const injectFromParams = (
	key,
	values,
	searchParams,
	paramKey = 'id',
) => {
	const idFromParams = new URLSearchParams(searchParams).get(paramKey);
	if (key in values && !values[key].includes(idFromParams)) {
		//TODO - need to ensure the id is actually in the list
		values[key].push(idFromParams);
	}
	return values;
};

/**
 * Pull a particular set of properties from an object
 * @param {Object} blob object to pull from
 * @param {string} propName target property name to extract from `blob`
 * @returns streamlined object containing only the target properties
 */
export const extractValuesObject = (blob, propName) => {
	const obj = {};
	Object.entries(blob).forEach(([key, value]) => {
		if (!(key in obj)) {
			obj[key] = value[propName] ?? '';
		}
	});
	return obj;
};

/**
 * Return the values from an object
 * @param {Object} blob object to pull from
 * @returns array of values from an object
 */
export const extractValuesArray = blob => {
	return Object.values(blob);
};

/**
 * Return the keys from an object
 * @param {Object} blob object to pull from
 * @returns array of keys from an object
 */
export const extractValuesArrayKeys = blob => {
	return Object.keys(blob);
};

/**
 * Check if a particular step contains fields with errors
 * @param {Object} state form state
 * @param {number} index step index
 * @param {Object} stepBlob object representing the steps
 * @param {Object} schemaBlob object representing the validation schema
 * @returns Boolean indicator of errors detected
 */
export const isStepError = (state, index, stepBlob, schemaBlob) => {
	//get all fields for current step
	const currentStepName = Object.values(stepBlob)[index];
	const fieldsOfInterest = Object.fromEntries(
		Object.entries(schemaBlob).filter(
			([key, value]) => value.stepLocation === currentStepName,
		),
	);

	//check if those fields have errors
	return Object.keys(fieldsOfInterest).some(
		fieldName => fieldName in state?.touched && fieldName in state?.errors,
	);
};

/**
 * Check if the form has errors
 * @param {Object} state form state
 * @returns whether the form has errors
 */
export const hasErrors = state => {
	return Object.values(state?.errors ?? {}).length > 0;
};

/**
 * Determine which (if any) step to move to on click
 * @param {string} type navigation type indicator
 * @param {number} activeStep current step index
 * @param {number} totalSteps total step count
 * @returns the next step index
 */
export const navigate = (
	type,
	activeStep,
	totalSteps,
	SubmitForm,
	CancelForm,
	GoHome,
	goToStep = null,
) => {
	switch (type) {
		case NAVIGATION_ACTIONS.SUBMIT:
			SubmitForm?.();
			return totalSteps - 1;
		case NAVIGATION_ACTIONS.NEXT:
			return Math.min(activeStep + 1, totalSteps - 1);
		case NAVIGATION_ACTIONS.PREVIOUS:
			return Math.max(activeStep - 1, 0);
		case NAVIGATION_ACTIONS.RESTART:
			CancelForm?.();
			return 0;
		case NAVIGATION_ACTIONS.CANCEL:
			alert('Cancelling will nullify your current transaction.');
			CancelForm?.();
			return 0;
		case NAVIGATION_ACTIONS.HOME:
			GoHome?.();
			break;
		case NAVIGATION_ACTIONS.GOTO:
			return goToStep !== null ? goToStep : activeStep;
		default:
			return activeStep;
	}
};

/**
 * simple comparator evaluation for sorting
 * @param {Object} a
 * @param {Object} b
 * @param {string} orderBy property to compare
 * @returns -1, 1, or 0
 */
export function descendingComparator(a, b, orderBy) {
	if (b[orderBy] < a[orderBy]) {
		return -1;
	}
	if (b[orderBy] > a[orderBy]) {
		return 1;
	}
	return 0;
}

/**
 * Get a function to compare two elements by a given property in a given direction
 * @param {string} order 'desc' for descending or 'asc' for ascending
 * @param {string} orderBy property to sort by
 * @returns function to sort
 */
export function getComparator(order, orderBy) {
	return order === 'desc'
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy);
}

/**
 * stable array sorting algorithm
 * @param {any[]} array array to sort
 * @param {function} comparator sorting function
 * @returns a sorted version of the array
 */
export function stableSort(array, comparator) {
	const stabilizedThis = array.map((el, index) => [el, index]);
	stabilizedThis.sort((a, b) => {
		const order = comparator(a[0], b[0]);
		if (order !== 0) {
			return order;
		}
		return a[1] - b[1];
	});
	return stabilizedThis.map(el => el[0]);
}

/**
 * Determine if a given date is within a given threshold number of days
 * @param {Date} date the target date in question
 * @param {Number} threshold number of days from today to determine if the given date passes validation
 * @returns true if and only if the given date is within the given threshold of days
 */
export const isDateWithinNDays = (date, threshold = 30) => {
	const today = new Date();
	const futureDate = new Date();
	futureDate.setDate(today.getDate() + threshold);
	const validity = date >= today && date <= futureDate;
	const difference = Math.round(
		(date.getTime() - today.getTime()) / (1000 * 3600 * 24),
	);
	return { expiresSoon: validity, difference: difference };
};

/**
 * process and massage the direct reports' data from the API for use client-side
 * @param {object[]} values direct reports' metadata
 * @returns staff data ready to display on the data-table
 */
export const processStaffDataForTable = values => {
	return values.map(dataset => {
		const { expiresSoon, difference } = isDateWithinNDays(
			new Date(dataset.dateValidationExpires),
			30,
		);

		const compliance = dataset?.isCompliant
			? expiresSoon
				? STATUS_TYPES.VALID_EXPIRING_SOON
				: STATUS_TYPES.VALID
			: STATUS_TYPES.OVERDUE;

		return {
			...dataset,
			isSelected: false,
			id: dataset.fiId,
			name: dataset.lastName
				? dataset.lastName + ', ' + dataset.firstName
				: null,
			status: compliance,
			daysUntilExpiration: difference,
			department: dataset.departmentName,
			jobTitle: dataset.jobTitle,
			companyName: dataset.companyName ? dataset.companyName : 'N/A',
			delegatedReviewerName: dataset.delegatedReviewer?.delegatedToName,
			delegatedReviewerMessage:
				dataset.delegatedReviewer?.delegatedToType,
		};
	});
};
